const CLICK_ID_COOKIE_DAYS = 90;

const TWITTER_CLICK_ID_PARAM = 'twclid';
// X's pixel stores the click id here. Our own cookie uses the query-param name.
const TWITTER_PIXEL_COOKIE = '_twclid';

const MICROSOFT_CLICK_ID_PARAM = 'msclkid';
// UET stores the click id with a session suffix (`-1`, `-0`, or `-1N`).
const UET_MICROSOFT_CLICK_ID_COOKIE = '_uetmsclkid';
const UET_CLICK_ID_SUFFIX = /-(?:1N|0|1)$/;

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || undefined;
  }
  return undefined;
};

const writeCookie = (name: string, value: string, days: number) => {
  if (typeof document === 'undefined') {
    return;
  }
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
};

/**
 * UET's cookie is not the raw click id. Bing CAPI matches the id from the
 * landing URL, so drop the suffix UET appends and ignore the empty sentinel.
 */
export const microsoftClickIdFromUetCookie = (
  value: string | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }
  if (decoded === 'N') {
    return undefined;
  }
  const stripped = decoded.replace(UET_CLICK_ID_SUFFIX, '');
  if (!stripped || stripped === 'N') {
    return undefined;
  }
  return stripped;
};

/**
 * X's pixel cookie is URL-encoded JSON (`{"pixelVersion":…,"twclid":"…"}`), not
 * the raw id. Conversions only match on the bare id, so unwrap it and never
 * pass the JSON through.
 */
export const twitterClickIdFromPixelCookie = (
  value: string | undefined,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }
  if (!decoded.trimStart().startsWith('{')) {
    return decoded;
  }
  try {
    const twclid = JSON.parse(decoded)?.twclid;
    return typeof twclid === 'string' && twclid ? twclid : undefined;
  } catch {
    return undefined;
  }
};

const sessionKey = (param: string) => `adClickId:${param}`;

const readSession = (param: string): string | undefined => {
  try {
    return window.sessionStorage.getItem(sessionKey(param)) || undefined;
  } catch {
    return undefined;
  }
};

const writeSession = (param: string, value: string) => {
  try {
    window.sessionStorage.setItem(sessionKey(param), value);
  } catch {
    // Private mode can block storage. The cookie is the copy that survives it.
  }
};

const persistClickId = (
  param: string,
  fallbackCookie: string | undefined,
  normalizeFallback: (value: string | undefined) => string | undefined,
): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const fromQuery =
    new URLSearchParams(window.location.search).get(param) || undefined;
  const fromCookie = readCookie(param);
  const fromFallback = fallbackCookie
    ? normalizeFallback(readCookie(fallbackCookie))
    : undefined;

  // The landing URL is this visit's click. Remember it for the tab so a later
  // signup step, which drops the query, does not swap in an older pixel cookie.
  if (fromQuery) {
    writeCookie(param, fromQuery, CLICK_ID_COOKIE_DAYS);
    writeSession(param, fromQuery);
    return fromQuery;
  }

  const fromSession = readSession(param);
  if (fromSession) {
    return fromSession;
  }

  // Query was already gone when this tab opened. The pixel cookie is the
  // click from that visit; our cookie may still be a previous one.
  if (fromFallback) {
    if (fromFallback !== fromCookie) {
      writeCookie(param, fromFallback, CLICK_ID_COOKIE_DAYS);
    }
    return fromFallback;
  }

  return fromCookie;
};

/**
 * X ads append `twclid` on the landing URL. Signup and Google sign-in rebuild
 * that URL without it, so persist a first-party cookie as soon as we see it.
 * The X pixel cookie is the fallback when the query param is already gone.
 */
export const persistTwitterClickId = (): string | undefined =>
  persistClickId(
    TWITTER_CLICK_ID_PARAM,
    TWITTER_PIXEL_COOKIE,
    twitterClickIdFromPixelCookie,
  );

/**
 * Microsoft Ads appends `msclkid`. Same URL-rebuild problem as `twclid`.
 * `_uetmsclkid` is the fallback when the UET tag already stored the click.
 */
export const persistMicrosoftClickId = (): string | undefined =>
  persistClickId(
    MICROSOFT_CLICK_ID_PARAM,
    UET_MICROSOFT_CLICK_ID_COOKIE,
    microsoftClickIdFromUetCookie,
  );

export const persistAdClickIds = (): void => {
  persistTwitterClickId();
  persistMicrosoftClickId();
};
